'use client';

// GRILLE SIGNATURE — le geste central de Remember, montré plutôt que décrit.
//
// Ce n'est pas une mosaïque décorative : c'est la VRAIE grammaire de l'éditeur de souvenirs
// (cahier §8) — des moments de largeur 1 à 4, dont la somme ne dépasse jamais 4 par ligne, un
// moment par type (texte OU photo OU vidéo), plus les deux cas qui font l'originalité du produit :
// le moment PRIVÉ (visible du seul propriétaire, remplacé ailleurs par un placeholder verrouillé
// à sa position, §8.7) et la CASE VIDE volontaire qui aère la mise en page (§8.5).
//
// Les cases se posent une à une (stagger) : le visiteur voit un souvenir se composer.
// Les dégradés sont DÉCORATIFS (ils évoquent des photos) — jamais des tokens d'UI.

import { Lock, MapPin, MessageCircle, Play } from 'lucide-react';

import { useT } from '@/hooks/useT';
import { fontStyle } from '@/theme/typography';

// Dégradés « photo » : lumière de voyage, chauds puis océaniques. Cohérents avec la palette
// crème / encre / menthe sans jamais réutiliser les tokens sémantiques de l'interface.
const SCENES = {
  lagoon:
    'linear-gradient(155deg, #F7C879 0%, #EE9758 34%, #C8613C 58%, #2F6491 100%)',
  dawn: 'linear-gradient(150deg, #F0C56E 0%, #A9B65E 52%, #3C7357 100%)',
  dive: 'linear-gradient(135deg, #4A6690 0%, #2C3D5B 55%, #18202F 100%)',
  reef: 'linear-gradient(160deg, #5FBFA8 0%, #2E7F92 60%, #1B4A63 100%)',
} as const;

// Chaque tuile porte sa position dans la grille 4 colonnes + son rang d'apparition.
// `span` = largeur en colonnes (1–4) ; la somme par ligne vaut toujours 4 → la règle du cahier
// est respectée à la lettre, y compris visuellement.
interface TileProps {
  span: 1 | 2 | 3 | 4;
  rows?: 1 | 2;
  order: number;
  className?: string;
  children: React.ReactNode;
}

function Tile({ span, rows = 1, order, className = '', children }: TileProps) {
  const spanClass = {
    1: 'col-span-1',
    2: 'col-span-2',
    3: 'col-span-3',
    4: 'col-span-4',
  }[span];
  const rowClass = rows === 2 ? 'row-span-2' : 'row-span-1';

  return (
    <figure
      className={`rmbr-tile-scroll relative overflow-hidden rounded-lg ${spanClass} ${rowClass} ${className}`}
      style={{ ['--rmbr-stagger' as string]: `${order * 110}ms` }}
    >
      {children}
    </figure>
  );
}

export function MemoryGrid() {
  const { t } = useT('landing');

  return (
    // `aspect-[4/4.6]` est INDISPENSABLE : les lignes sont en `1fr`, or sans hauteur imposée au
    // conteneur elles se calent sur leur contenu — et les tuiles purement décoratives (photo,
    // vidéo, case vide) n'en ont aucun, donc la grille s'effondrerait à quelques pixels.
    <div className="grid aspect-[4/4.6] grid-cols-4 grid-rows-[repeat(4,minmax(0,1fr))] gap-2.5 sm:gap-3">
      {/* Ligne 1-2 — couverture (3) + texte (1) : 3 + 1 = 4 ✓ */}
      <Tile
        span={3}
        rows={2}
        order={0}
        className="shadow-[var(--rmbr-shadow-xl)]"
      >
        <div
          className="absolute inset-0"
          style={{ background: SCENES.lagoon }}
        />
        <div
          className="absolute inset-x-0 bottom-0 h-3/5"
          style={{
            background:
              'linear-gradient(to top, var(--rmbr-cover-scrim), transparent)',
          }}
        />
        <span
          className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-black/30 px-2.5 py-1 text-on-media backdrop-blur-md"
          style={fontStyle('caption')}
        >
          <span aria-hidden className="size-1.5 rounded-full bg-brand" />
          {t('grid.shared')}
        </span>
        <figcaption className="absolute inset-x-0 bottom-0 flex flex-col gap-1 p-4 sm:p-5">
          <span
            className="text-on-media"
            style={{
              fontFamily: 'var(--font-serif)',
              fontSize: 'clamp(1.25rem, 2.2vw, 1.75rem)',
              lineHeight: 1.1,
              fontWeight: 600,
              letterSpacing: '-0.01em',
            }}
          >
            {t('grid.title')}
          </span>
          <span
            className="flex items-center gap-1.5 text-on-media-muted"
            style={fontStyle('bodySm')}
          >
            <MapPin className="size-3.5 shrink-0" />
            {t('grid.place')}
          </span>
        </figcaption>
      </Tile>

      {/* Moment TEXTE — le seul type qui n'est pas une image : la voix du souvenir. */}
      <Tile
        span={1}
        rows={2}
        order={1}
        className="flex flex-col justify-between border border-border bg-surface p-3 shadow-[var(--rmbr-shadow-card)] sm:p-4"
      >
        <span
          aria-hidden
          className="text-brand"
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 30,
            lineHeight: 1,
          }}
        >
          &ldquo;
        </span>
        <p
          className="text-text-secondary"
          style={{
            fontFamily: 'var(--font-serif)',
            fontStyle: 'italic',
            fontSize: 'clamp(0.72rem, 1vw, 0.85rem)',
            lineHeight: 1.45,
          }}
        >
          {t('grid.note')}
        </p>
        <span className="text-text-tertiary" style={fontStyle('caption')}>
          {t('grid.noteAuthor')}
        </span>
      </Tile>

      {/* Ligne 3 — photo (2) + vidéo (2) : 2 + 2 = 4 ✓ */}
      <Tile span={2} order={2} className="shadow-[var(--rmbr-shadow-card)]">
        <div className="absolute inset-0" style={{ background: SCENES.dawn }} />
        <div
          className="absolute inset-x-0 bottom-0 h-2/3"
          style={{
            background:
              'linear-gradient(to top, var(--rmbr-cover-scrim-soft), transparent)',
          }}
        />
        {/* Interactions : likes + commentaires vivent SUR le moment, pas ailleurs (§10). */}
        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 p-3">
          <span className="text-on-media" style={fontStyle('bodySmMedium')}>
            {t('grid.photo')}
          </span>
          <span
            className="flex items-center gap-1 rounded-full bg-black/30 px-2 py-0.5 text-on-media backdrop-blur-md"
            style={fontStyle('caption')}
          >
            <MessageCircle className="size-3" />3
          </span>
        </div>
      </Tile>

      <Tile span={2} order={3} className="shadow-[var(--rmbr-shadow-card)]">
        <div className="absolute inset-0" style={{ background: SCENES.dive }} />
        <div className="absolute inset-0 flex items-center justify-between px-3 sm:px-4">
          <span className="grid size-9 place-items-center rounded-full bg-on-media/90 text-[color:var(--rmbr-media-backdrop)] shadow-[var(--rmbr-shadow-md)] sm:size-10">
            <Play className="size-4 translate-x-px fill-current" />
          </span>
          <span className="text-on-media" style={fontStyle('bodySmMedium')}>
            {t('grid.video')}
          </span>
        </div>
      </Tile>

      {/* Ligne 4 — privé (1) + vide (1) + photo (2) : 1 + 1 + 2 = 4 ✓
          Le moment PRIVÉ garde sa place dans la grille pour les autres : la mise en page reste
          intacte, seul le contenu est protégé. C'est le détail qui explique tout le produit. */}
      <Tile
        span={1}
        order={4}
        className="grid place-items-center border border-dashed border-locked bg-fill-subtle"
      >
        <div className="flex flex-col items-center gap-1.5 px-1 text-center">
          <Lock className="size-4 text-locked" />
          <span className="text-text-tertiary" style={fontStyle('caption')}>
            {t('grid.private')}
          </span>
        </div>
      </Tile>

      {/* Case VIDE volontaire : elle occupe sa place et n'existe que pour faire respirer. */}
      <Tile
        span={1}
        order={5}
        className="border border-border/70 bg-transparent"
      >
        <span className="sr-only">{t('grid.empty')}</span>
      </Tile>

      <Tile span={2} order={6} className="shadow-[var(--rmbr-shadow-card)]">
        <div className="absolute inset-0" style={{ background: SCENES.reef }} />
        <div
          className="absolute inset-x-0 bottom-0 h-2/3"
          style={{
            background:
              'linear-gradient(to top, var(--rmbr-cover-scrim-soft), transparent)',
          }}
        />
        <figcaption
          className="absolute inset-x-0 bottom-0 p-3 text-on-media"
          style={fontStyle('bodySmMedium')}
        >
          {t('grid.reef')}
        </figcaption>
      </Tile>
    </div>
  );
}
